import { useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { useSidebar } from '@/app/providers/SidebarProvider';
import { navItems, adminItems, visibleNavItems, type NavItem } from '@/app/nav';
import SidebarWidget from './SidebarWidget';
import { activeNavPath } from './activeNavPath';
import { submenuKey, useSidebarSubmenu, type MenuType } from './useSidebarSubmenu';
import MotorPlanLogo from '@shared/components/MotorPlanLogo';
import { ChevronDownIcon, Ellipsis } from 'lucide-react';

const AppSidebar: React.FC = () => {
    const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
    const { user, isInternal } = useAuth();
    const location = useLocation();

    const mainItems = useMemo(
        () => visibleNavItems(navItems, { user, isInternal }),
        [user, isInternal]
    );
    const otherItems = useMemo(
        () => visibleNavItems(adminItems, { user, isInternal }),
        [user, isInternal]
    );

    const activePath = useMemo(
        () => activeNavPath([...mainItems, ...otherItems], location.pathname),
        [mainItems, otherItems, location.pathname]
    );
    const isActive = useCallback((path: string) => path === activePath, [activePath]);
    const { openKey, toggle, registerPanel, panelHeight } = useSidebarSubmenu(
        mainItems,
        otherItems,
        location.pathname
    );

    const renderMenuItems = (items: NavItem[], menuType: MenuType) => (
        <ul className="flex flex-col gap-4">
            {items.map((nav) => {
                const key = submenuKey(menuType, nav.name);
                const isOpen = openKey === key;
                const panelId = `sidebar-submenu-${key}`;
                return (
                    <li key={nav.name}>
                        {nav.subItems ? (
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggle(key)}
                                className={`menu-item group ${
                                    isOpen ? 'menu-item-active' : 'menu-item-inactive'
                                } cursor-pointer ${
                                    !isExpanded && !isHovered
                                        ? 'lg:justify-center'
                                        : 'lg:justify-start'
                                }`}
                            >
                                <span
                                    className={`menu-item-icon-size  ${
                                        isOpen ? 'menu-item-icon-active' : 'menu-item-icon-inactive'
                                    }`}
                                >
                                    {nav.icon}
                                </span>
                                {(isExpanded || isHovered || isMobileOpen) && (
                                    <span className="menu-item-text">{nav.name}</span>
                                )}
                                {(isExpanded || isHovered || isMobileOpen) && (
                                    <ChevronDownIcon
                                        className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                                            isOpen ? 'rotate-180 text-mp-primary' : ''
                                        }`}
                                    />
                                )}
                            </button>
                        ) : (
                            nav.path && (
                                <Link
                                    to={nav.path}
                                    className={`menu-item group ${
                                        isActive(nav.path)
                                            ? 'menu-item-active'
                                            : 'menu-item-inactive'
                                    }`}
                                >
                                    <span
                                        className={`menu-item-icon-size ${
                                            isActive(nav.path)
                                                ? 'menu-item-icon-active'
                                                : 'menu-item-icon-inactive'
                                        }`}
                                    >
                                        {nav.icon}
                                    </span>
                                    {(isExpanded || isHovered || isMobileOpen) && (
                                        <span className="menu-item-text">{nav.name}</span>
                                    )}
                                </Link>
                            )
                        )}
                        {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
                            <div
                                id={panelId}
                                ref={(el) => registerPanel(key, el)}
                                inert={!isOpen}
                                aria-hidden={!isOpen}
                                className="overflow-hidden transition-all duration-300"
                                style={{ height: isOpen ? `${panelHeight(key)}px` : '0px' }}
                            >
                                <ul className="mt-2 space-y-1 ml-9">
                                    {nav.subItems.map((subItem) => (
                                        <li key={subItem.name}>
                                            <Link
                                                to={subItem.path}
                                                className={`menu-dropdown-item ${
                                                    isActive(subItem.path)
                                                        ? 'menu-dropdown-item-active'
                                                        : 'menu-dropdown-item-inactive'
                                                }`}
                                            >
                                                {subItem.name}
                                                <span className="flex items-center gap-1 ml-auto">
                                                    {subItem.new && (
                                                        <span
                                                            className={`ml-auto ${
                                                                isActive(subItem.path)
                                                                    ? 'menu-dropdown-badge-active'
                                                                    : 'menu-dropdown-badge-inactive'
                                                            } menu-dropdown-badge`}
                                                        >
                                                            new
                                                        </span>
                                                    )}
                                                    {subItem.pro && (
                                                        <span
                                                            className={`ml-auto ${
                                                                isActive(subItem.path)
                                                                    ? 'menu-dropdown-badge-active'
                                                                    : 'menu-dropdown-badge-inactive'
                                                            } menu-dropdown-badge`}
                                                        >
                                                            pro
                                                        </span>
                                                    )}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );

    return (
        <aside
            className={`fixed mt-16 flex flex-col lg:mt-0 top-(--env-banner-height) px-5 left-0 bg-mp-surface text-mp-foreground h-[calc(100vh-var(--env-banner-height)-4rem)] lg:h-[calc(100vh-var(--env-banner-height))] transition-all duration-300 ease-in-out z-50 border-r border-mp-border
        ${isExpanded || isMobileOpen ? 'w-[290px]' : isHovered ? 'w-[290px]' : 'w-[90px]'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
            onMouseEnter={() => !isExpanded && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {!isMobileOpen && (
                <div
                    className={`py-5 flex ${
                        !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-center'
                    }`}
                >
                    <Link to="/">
                        <MotorPlanLogo
                            showText={isExpanded || isHovered}
                            textSize="w-52"
                            iconSize="w-12"
                        />
                    </Link>
                </div>
            )}
            <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
                {isExpanded || isHovered || isMobileOpen ? <SidebarWidget /> : null}
                <nav className="mt-3 mb-6">
                    <div className="flex flex-col gap-4">
                        <div>
                            <h2
                                className={`mb-4 text-xs uppercase flex leading-[20px] text-mp-muted ${
                                    !isExpanded && !isHovered
                                        ? 'lg:justify-center'
                                        : 'justify-start'
                                }`}
                            >
                                {isExpanded || isHovered || isMobileOpen ? (
                                    'Menu'
                                ) : (
                                    <Ellipsis className="size-6" />
                                )}
                            </h2>
                            {renderMenuItems(mainItems, 'main')}
                        </div>
                        {otherItems.length > 0 && (
                            <div>
                                <h2
                                    className={`mb-4 text-xs uppercase flex leading-[20px] text-mp-muted ${
                                        !isExpanded && !isHovered
                                            ? 'lg:justify-center'
                                            : 'justify-start'
                                    }`}
                                >
                                    {isExpanded || isHovered || isMobileOpen ? (
                                        'Admin'
                                    ) : (
                                        <Ellipsis />
                                    )}
                                </h2>
                                {renderMenuItems(otherItems, 'others')}
                            </div>
                        )}
                    </div>
                </nav>
            </div>
        </aside>
    );
};

export default AppSidebar;
